import React, { useContext, useState } from "react";
import { UserContext } from "../../../UserProvider";
import { hasRole, UserLocation } from "../../../models/User";
import {
  Button,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  InputAdornment,
  LinearProgress,
  Snackbar,
  TextField,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import * as GTIN from "../../../models/GTIN";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import * as Result from "../../../result";
import useStyles from "./styles";
import Alert from "@material-ui/lab/Alert";
import TabletAndroidIcon from "@material-ui/icons/TabletAndroid";
import { AlertTitle } from "@material-ui/lab";
import { permissions } from "../../../constants";
import { StoreSelect } from "../../../components/selects/StoreSelect";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { ProductDetails } from "./ProductDetails";
import { generateEAN } from "../../statistics/shared/eanFunctions";
import { noop } from "../../../utils";
import { globalSettings } from "../../../models/Settings";
import { useI18n } from "../../../hooks/useI18n";
import { ScanditBarcodeScanner } from "../../../components/ScanditBarcodeScanner";
import { Symbology } from "@scandit/web-datacapture-barcode";

interface ManualInputState {
  value: string;
  error: boolean;
}

interface ScanProductProps {
  storeId?: string;
  storeName?: string;
  showScanner?: boolean;
  onScannerClosed?: Function;
}

export const ManageProduct = ({
  storeId,
  storeName,
  showScanner,
  onScannerClosed
}: ScanProductProps) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const classes = useStyles();
  const user = useContext(UserContext);
  const userLocation = useSelector<AppState, UserLocation>(s => s.userLocation);

  const [state, setState] = useState<any>({
    showScanner: showScanner,
    scanning: showScanner,
    showProduct: false,
    storeId: storeId || userLocation.storeId,
    storeName: storeName || userLocation.storeName,
    gtin: "",
    deleteSuccess: false,
    problem:
      user.type === "ANONYMOUS" || !hasRole(user, permissions.scanProduct)
        ? { status: 401, title: t("common.tableHeaders.unauthorizedAccess") }
        : null
  });

  const [manualInput, setManualInput] = useState<ManualInputState>({
    value: "",
    error: false
  });

  const onClose = () => {
    onScannerClosed ? onScannerClosed() : noop();
  };

  const loadProduct = (gtin?: string) => {
    if (!gtin) return;

    setState((s: any) => ({
      ...s,
      showProduct: true,
      gtin,
      scanning: false,
      showScanner: false
    }));
  };

  const generateBarcode = () => {
    generateEAN().then(ean => {
      if (ean) setManualInput({ value: ean, error: false });
    });
  };

  return (
    <>
      {state.problem?.status !== 401 && (
        <>
          <div className={classes.card}>
            <div className={classes.progressContainer}>
              <Fade
                in={state.loading}
                style={{
                  transitionDelay: state.loading ? "800ms" : "0ms"
                }}
                unmountOnExit
              >
                <LinearProgress />
              </Fade>
            </div>

            <StoreSelect
              label={t("common.formFields.selectStore")}
              value={state.storeId ?? ""}
              className={classes.select}
              filterOpened={true}
              onSelected={store =>
                setState((s: any) => ({
                  ...s,
                  storeId: store.id,
                  storeName: store.name
                }))
              }
            />

            <div>
              <Button
                className={classes.scanButton}
                disabled={state.loading || !state.storeId}
                variant="contained"
                color="primary"
                startIcon={<TabletAndroidIcon />}
                onClick={() => {
                  setManualInput({ error: false, value: "" });
                  setState((s: any) => ({
                    ...s,
                    showScanner: true,
                    scanning: true
                  }));
                }}
              >
                {t("common.buttons.scanProduct")}
              </Button>
            </div>
          </div>

          <Dialog
            fullWidth
            fullScreen={mobile}
            maxWidth="md"
            open={state.showScanner || state.showProduct}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              style: mobile
                ? { height: "100dvh", display: "flex", flexDirection: "column" }
                : { height: "80vh", display: "flex", flexDirection: "column" }
            }}
          >
            <MuiDialogTitle disableTypography>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => {
                  setState((s: any) => ({
                    ...s,
                    showScanner: false,
                    scanning: false,
                    showProduct: false
                  }));
                  onClose();
                }}
              >
                <CloseIcon />
              </IconButton>
            </MuiDialogTitle>

            {state.scanning && (
              <DialogContent
                style={{
                  flex: 1,
                  minHeight: 0,
                  padding: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {/* Scanner area */}
                <div
                  className={classes.scanner}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    position: "relative",
                    overflow: "hidden",
                    background: "#000",
                    marginTop: 15,
                    marginBottom: mobile ? 0 : 0
                  }}
                >
                  {state.showScanner && (
                    <ScanditBarcodeScanner
                      licenseKey={
                        globalSettings.settings.scandit.scannerLicenseKey
                      }
                      libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
                      style={{ width: "100%", height: "100%" }}
                      paused={state.loading}
                      onScan={(data: string, symbology: Symbology) => {
                        if (!data) return;
                        loadProduct(data);
                        setManualInput(s => ({
                          ...s,
                          value: data,
                          error: false
                        }));
                      }}
                    />
                  )}
                </div>

                {/* Manual input footer */}
                <div
                  style={{
                    flex: "0 0 auto",
                    padding: "12px 16px",
                    paddingBottom: "max(12px, env(safe-area-inset-bottom))",
                    borderTop: "1px solid #ddd",
                    background: "#f5f5f5"
                  }}
                >
                  <TextField
                    fullWidth
                    disabled={state.loading || !state.storeId}
                    className={classes.manualInput}
                    error={manualInput.error}
                    type="number"
                    label={t("common.formFields.manualInput")}
                    value={manualInput.value}
                    inputProps={{ style: { fontSize: 16 } }} // prevent iOS focus zoom
                    InputLabelProps={{
                      className: classes.manualInputLabel,
                      style: { fontSize: 16 }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            disabled={state.loading || !state.storeId}
                            onClick={() => {
                              const gtinParsed = GTIN.fromString(
                                manualInput.value
                              );
                              const val = Result.coerce(gtinParsed);

                              if (!val) {
                                setManualInput(s => ({ ...s, error: true }));
                                return;
                              }
                              loadProduct(manualInput.value);
                            }}
                          >
                            OK
                          </Button>
                        </InputAdornment>
                      )
                    }}
                    onChange={e =>
                      setManualInput({ value: e.target.value, error: false })
                    }
                  />

                  <div style={{ textAlign: "right" }}>
                    <Button color="primary" onClick={generateBarcode}>
                      {t("common.buttons.generateBarcode")}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            )}

            {state.showProduct && (
              <DialogContent
                style={{ padding: "10px 0 0 0", overflow: "auto" }}
              >
                <ProductDetails
                  storeId={state.storeId}
                  storeName={state.storeName}
                  gtin={state.gtin}
                  onSave={() =>
                    setState((s: any) => ({ ...s, showProduct: true }))
                  }
                  onDelete={() =>
                    setState((s: any) => ({
                      ...s,
                      showProduct: false,
                      showScanner: false,
                      scanning: false,
                      deleteSuccess: true
                    }))
                  }
                />
              </DialogContent>
            )}
          </Dialog>
        </>
      )}

      <Snackbar
        open={state.deleteSuccess}
        autoHideDuration={3000}
        onClose={() => setState((s: any) => ({ ...s, deleteSuccess: false }))}
      >
        <Alert severity="success">
          {t("common.messages.productHasBeenRemoved")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={state.problem != null}
        autoHideDuration={6000}
        onClose={() => setState((s: any) => ({ ...s, problem: null }))}
      >
        <Alert severity="error">
          <AlertTitle>
            {state.problem?.status}, {state.problem?.title}
          </AlertTitle>
          {state.problem?.detail} <strong>{state.problem?.traceId}</strong>
        </Alert>
      </Snackbar>
    </>
  );
};
